import React from "react";
import { Dialog, DialogHeader, DialogBody, DialogFooter, Typography } from "@material-tailwind/react";
import { motion, AnimatePresence } from "framer-motion";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { EASING } from "../../utils/easingUtils";

interface DeleteModalProps {
  open: boolean;
  handleClose: () => void;
  handleDelete: () => void;
  category: { id: any; designation: string } | null;
  isLoading?: boolean;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ open, handleClose, handleDelete, category, isLoading }) => {
  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          handler={handleClose}
          className="w-11/12 max-w-md overflow-hidden transition-all transform bg-white shadow-2xl dark:bg-gray-900 rounded-2xl"
          size="sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: EASING.easeOut }}
          >
            <DialogHeader className="p-4 border-b border-gray-200 dark:border-gray-800">
              <Typography
                variant="h5"
                className="w-full font-bold tracking-tight text-center text-gray-900 dark:text-gray-100"
              >
                Confirmer la suppression
              </Typography>
            </DialogHeader>
            <DialogBody className="grid gap-6 p-8 text-gray-700 place-items-center dark:text-gray-200">
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-16 h-16 text-red-500 dark:text-red-400"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <path
                  fillRule="evenodd"
                  d="M5.25 9a6.75 6.75 0 0113.5 0v.75c0 2.123.8 4.057 2.118 5.52a.75.75 0 01-.297 1.206c-1.544.57-3.16.99-4.831 1.243a3.75 3.75 0 11-7.48 0 24.585 24.585 0 01-4.831-1.244.75.75 0 01-.298-1.205A8.217 8.217 0 005.25 9.75V9zm4.502 8.9a2.25 2.25 0 104.496 0 25.057 25.057 0 01-4.496 0z"
                  clipRule="evenodd"
                />
              </motion.svg>
              <Typography variant="h4" className="font-semibold text-center text-gray-900 dark:text-gray-100">
                Êtes-vous sûr ?
              </Typography>
              <Typography className="text-base leading-relaxed text-center text-gray-600 dark:text-gray-300">
                La suppression de{" "}
                <span className="font-medium text-red-600 dark:text-red-400">{category?.designation}</span> est
                irréversible et ne peut pas être annulée.
              </Typography>
            </DialogBody>
            <DialogFooter className="flex justify-center p-6 space-x-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
              <ButtonSecondary
                onClick={handleClose}
                className="px-6 py-3 font-medium text-gray-700 transition-all duration-300 transform bg-gray-200 rounded-full dark:text-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 hover:scale-105"
              >
                Annuler
              </ButtonSecondary>
              <ButtonPrimary
                onClick={handleDelete}
                loading={isLoading}
                className="px-6 py-3 font-medium text-white transition-all duration-300 transform rounded-full bg-gradient-to-r from-red-500 to-red-600 dark:from-red-600 dark:to-red-700 hover:from-red-600 hover:to-red-700 dark:hover:from-red-700 dark:hover:to-red-800 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                Supprimer
              </ButtonPrimary>
            </DialogFooter>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default DeleteModal;
